import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, getDashboardRoute } from "../App";
import { Button } from "../components/ui/button";
import { Building2, Users, Home, Briefcase, TrendingUp } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

const ROLES = [
  {
    id: "agent",
    label: "Agent",
    description: "Submit and manage property listings",
    icon: Users,
    color: "bg-blue-500",
  },
  {
    id: "seller",
    label: "Seller",
    description: "List properties for sale",
    icon: Home,
    color: "bg-green-500",
  },
  {
    id: "wholesaler",
    label: "Wholesaler",
    description: "Submit and manage deals",
    icon: Briefcase,
    color: "bg-purple-500",
  },
  {
    id: "investor",
    label: "Investor",
    description: "Browse and invest in deals",
    icon: TrendingUp,
    color: "bg-orange-500",
  },
];

const RoleSelectionPage = () => {
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(false);
  const { updateRole, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRole) return;

    setLoading(true);
    try {
      const updatedUser = await updateRole(selectedRole);
      navigate(getDashboardRoute(updatedUser.role), { replace: true });
    } catch (error) {
      // Error handled in auth context
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container bg-background">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="auth-card animate-scale-in">
        <div className="auth-header">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="auth-title font-heading">Welcome, {user?.name}!</h1>
          <p className="auth-subtitle">Select your role to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            {ROLES.map((role) => {
              const Icon = role.icon;
              return (
                <div
                  key={role.id}
                  className={`role-option ${selectedRole === role.id ? "selected" : ""}`}
                  onClick={() => setSelectedRole(role.id)}
                  data-testid={`role-select-${role.id}`}
                >
                  <div className={`role-option-icon ${role.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="role-option-content">
                    <div className="role-option-title">{role.label}</div>
                    <div className="role-option-desc">{role.description}</div>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedRole === role.id
                        ? "border-blue-500 bg-blue-500"
                        : "border-muted-foreground"
                    }`}
                  >
                    {selectedRole === role.id && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-500 hover:bg-blue-600 mt-6"
            disabled={loading || !selectedRole}
            data-testid="role-submit-button"
          >
            {loading ? (
              <div className="loading-spinner border-white"></div>
            ) : (
              "Continue to Dashboard"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
